import { CSSProperties, FC, memo, useEffect, useState } from "react";
import { styled } from "@mui/material";
import portrait from "../../assets/landing/hero-portrait.webp";
import { featuredIn, heroDisciplines, mailTo } from "../../utils/landing-data";
import { bg, bp, fluid, font, w } from "../../styles/landing-tokens";
import { DisciplineRow, GhostButton, PrimaryButton } from "./shared";
import { BookIcon, PlayOutline } from "./icons";
import { EASE, REDUCED } from "./reveal";
import { useParallax } from "../../hooks/use-parallax";
import { useWindowSize } from "../../hooks/use-window-size";

/**
 * Below this width the hero's three action buttons would collide with the
 * absolutely-positioned portrait, so the portrait drops into the flow instead.
 */
const stacked = "@media (max-width: 1280px)";

/** Figma: FRAME "Section" 1920×1734, #060B14, clipped. */
const Section = styled("section")({
  position: "relative",
  background: bg,
  overflow: "hidden",
  minHeight: 1203,

  [stacked]: {
    minHeight: 0,
    paddingTop: 120,
  },
});

const Inner = styled("div")({
  position: "relative",
  width: "100%",
  /** Figma content 1568 wide (x 176→1744) plus the 40px gutter. */
  maxWidth: 1648,
  margin: "0 auto",
  padding: "0 40px",
  display: "flex",
  alignItems: "flex-start",
  /** Figma: the hero block runs from y=0 to the "Featured in" strip at y=1203. */
  minHeight: 1203,

  [stacked]: {
    minHeight: 0,
    flexDirection: "column",
  },
  [bp.sm]: {
    padding: "0 20px",
  },
});

const Copy = styled("div")({
  position: "relative",
  zIndex: 2,
  paddingTop: 356,
  width: "100%",
  maxWidth: 875,

  [bp.lg]: { paddingTop: 260 },
  [stacked]: { paddingTop: 40, maxWidth: "100%" },
});

/** "Evan Szu" — DM Sans 600, 105.3/105.3, -2px tracking. */
const Title = styled("h1")({
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: fluid(52, 105.3),
  lineHeight: 1,
  letterSpacing: "-0.019em",
  color: "#FFFFFF",
  margin: 0,
});

const Lead = styled("p")({
  maxWidth: 727,
  marginTop: 30,
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: 20,
  lineHeight: "42.6px",
  color: w(0.5),

  [bp.sm]: {
    fontSize: 16,
    lineHeight: "30px",
  },
});

const Actions = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 17.5,
  /** Figma: lead ends at y=620, buttons start at y=741. */
  marginTop: 121,

  [bp.sm]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
    marginTop: 40,
  },
});

/** 2×2 grid of disciplines: 421px columns, 430px column pitch, 68px row pitch. */
const Disciplines = styled("div")({
  display: "grid",
  gridTemplateColumns: "421px 421px",
  columnGap: 9,
  rowGap: 16,
  /** Figma: buttons end at y=798, first discipline row starts at y=847. */
  marginTop: 49,

  /** Below 1920 the portrait moves inward, so drop to a single column. */
  [bp.lg]: {
    gridTemplateColumns: "1fr",
    rowGap: 4,
    maxWidth: 560,
  },
});

/**
 * The portrait plus the soft white ellipse glow behind it.
 * Figma: image 695×1020 @(1093,189); Ellipse 704×767 @(1108,482), white 40%, blur 250.
 */
const PortraitWrap = styled("div")({
  position: "absolute",
  top: 189,
  /** Figma x=1093 on a 1920 frame → 133px right of centre. */
  left: "calc(50% + 133px)",
  width: 695,
  height: 1020,
  zIndex: 1,

  /** Narrow enough to clear the 797px-wide button row down to 1281px. */
  [bp.lg]: {
    left: "auto",
    right: 0,
    width: 470,
    height: 690,
    top: 200,
  },
  [stacked]: {
    position: "relative",
    left: "auto",
    right: "auto",
    top: 0,
    margin: "40px auto 0",
    width: "min(420px, 90%)",
    height: "auto",
  },
});

const Glow = styled("div")({
  position: "absolute",
  top: 293,
  left: 15,
  width: 704,
  height: 767,
  borderRadius: "50%",
  background: w(0.4),
  /** Figma layer blur 250 spreads less than the CSS filter — dialled back to match. */
  filter: "blur(170px)",
  pointerEvents: "none",
  animation: "glowPulse 8s ease-in-out infinite",

  [stacked]: { display: "none" },
});

/** Figma fills the 695×1020 rectangle with scaleMode STRETCH. */
const Portrait = styled("img")({
  position: "relative",
  width: "100%",
  height: "100%",
  objectFit: "fill",

  [stacked]: { objectFit: "contain" },
});

/** Figma: 1920×121 strip, radial gradient #060B14 → #20252C. */
const FeaturedStrip = styled("div")({
  position: "relative",
  zIndex: 3,
  height: 121,
  display: "flex",
  alignItems: "center",
  background: "radial-gradient(120% 300% at 50% 160%, #20252C 0%, #060B14 100%)",

  [bp.md]: {
    height: "auto",
    padding: "24px 0",
  },
});

const StripInner = styled("div")({
  width: "100%",
  /** Figma: label starts at x=153 and the last outlet ends at x=1756. */
  maxWidth: 1694,
  margin: "0 auto",
  padding: "0 51px 0 40px",
  display: "flex",
  alignItems: "center",

  [bp.md]: {
    flexDirection: "column",
    gap: 16,
    padding: "0 40px",
  },
  [bp.sm]: { padding: "0 20px" },
});

const FeaturedLabel = styled("span")({
  fontFamily: font.sans,
  fontWeight: 900,
  fontSize: 14,
  lineHeight: "18.8px",
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
});

/**
 * Figma: the label ends at x=283 and the outlets run 569→1756, so the group is
 * offset 286px from the label and spans the rest of the row.
 */
const Outlets = styled("div")({
  flex: 1,
  marginLeft: 294,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  [bp.lg]: { marginLeft: 60 },
  [bp.md]: {
    marginLeft: 0,
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 20,
  },
});

const Outlet = styled("span")({
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "22.6px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "#FFFFFF",
  whiteSpace: "nowrap",

  [bp.sm]: { fontSize: 11, letterSpacing: "0.12em" },
});

export const Hero: FC = memo(() => {
  /** Drive the on-load entrance: render hidden once, then flip after first paint. */
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const { width } = useWindowSize();
  const { ref: portraitRef, offset } = useParallax(0.06, width > 0 && width <= 1280);

  /** Staggered fade-and-rise for a hero line. */
  const enter = (delay: number, y = 24): CSSProperties | undefined =>
    REDUCED
      ? undefined
      : {
          opacity: entered ? 1 : 0,
          transform: entered ? "none" : `translate3d(0, ${y}px, 0)`,
          transition: `opacity 0.8s ${EASE} ${delay}ms, transform 0.8s ${EASE} ${delay}ms`,
        };

  return (
    <Section id="home">
      <Inner>
        <Copy>
          <Title style={enter(0)}>Evan Szu</Title>

          <Lead style={enter(120)}>
            Three decades of professional derivatives trading. A Stanford doctorate. Two
            published books. A track record of building at the intersection of disciplines -
            now democratizing institutional yield through Gamma Prime.
          </Lead>

          <Actions style={enter(240)}>
            <PrimaryButton href={mailTo("Getting in touch with Evan Szu")}>
              Get in Touch
            </PrimaryButton>
            <GhostButton href="#books">
              <BookIcon /> Read the Books
            </GhostButton>
            <GhostButton href="#interviews">
              <PlayOutline /> Watch
            </GhostButton>
          </Actions>

          <Disciplines style={enter(360)}>
            {heroDisciplines.map((d) => (
              <DisciplineRow key={d.no}>
                <span className="no">{d.no}</span>
                <span className="name">{d.name}</span>
                <span className="meta">{d.meta}</span>
              </DisciplineRow>
            ))}
          </Disciplines>
        </Copy>

        <PortraitWrap
          ref={portraitRef}
          style={
            REDUCED
              ? undefined
              : {
                  opacity: entered ? 1 : 0,
                  transform: `translate3d(0, ${offset}px, 0)`,
                  transition: `opacity 1.1s ${EASE} 200ms`,
                }
          }
        >
          <Glow />
          <Portrait src={portrait} alt="Evan Szu" />
        </PortraitWrap>
      </Inner>

    <FeaturedStrip>
      <StripInner>
        <FeaturedLabel>Featured in</FeaturedLabel>
        <Outlets>
          {featuredIn.map((name) => (
            <Outlet key={name}>{name}</Outlet>
          ))}
        </Outlets>
      </StripInner>
    </FeaturedStrip>
    </Section>
  );
});
